import { mapDTO } from '@twistezo/ts-dto-mapper'

import { SortInput } from 'components/Table'
import { alertsFromDTO } from 'features/alerts/api/mappers/alertFromDTO'
import { parsePrimitiveValue } from 'features/globalSetup/utils/parameter'
import {
  DomainPrebidModuleParamsDTO,
  DomainPrebidModuleParamsSchemaDTO,
} from 'features/inventory/domain/api/types/domainPrebidModuleDTO'
import { DomainAlert } from 'features/inventory/domain/types/DomainAlert'
import {
  DomainPrebidModuleParameter,
  DomainPrebidModuleParameters,
  DomainPrebidModuleSchemaParameter,
} from 'features/inventory/domain/types/domainPrebidModule'
import { DomainPrebidModuleAlert } from 'features/inventory/domain/types/domainPrebidModuleAlert'
import { isPrimitive } from 'utils/primitive'
import { DomainPrebidModuleSlice, DomainPrebidModuleSortBy } from '../types'
import { DomainPrebidModuleSliceDTO, DomainPrebidModuleSortByDTO } from './types'

export const domainPrebidModulesFromDTO = (
  from: DomainPrebidModuleSliceDTO,
): DomainPrebidModuleSlice =>
  mapDTO<DomainPrebidModuleSliceDTO, DomainPrebidModuleSlice>({ from }).transform(
    (data: DomainPrebidModuleSliceDTO): DomainPrebidModuleSlice => {
      const {
        domain: { activeAbTest, alerts: domainAlerts, name },
        enabled,
        fieldAlerts,
        id,
        params,
        paramsSchema: paramsSchemaDTO,
        updatedAt,
      } = data

      const paramsSchema: DomainPrebidModuleSchemaParameter[] = paramsSchemaFromDTO(paramsSchemaDTO)

      return {
        alerts: alertsFromDTO<DomainPrebidModuleAlert>(fieldAlerts),
        domain: {
          activeAbTest: activeAbTest ?? undefined,
          alerts: alertsFromDTO<DomainAlert>(domainAlerts),
          name,
        },
        enabled,
        id,
        params: paramsFromDTO(params, paramsSchema),
        paramsSchema,
        updatedAt: new Date(updatedAt),
      }
    },
  )

export const paramsSchemaFromDTO = (
  paramsSchema: DomainPrebidModuleParamsSchemaDTO[],
): DomainPrebidModuleSchemaParameter[] =>
  paramsSchema.map(
    (paramDTO: DomainPrebidModuleParamsSchemaDTO): DomainPrebidModuleSchemaParameter => {
      const { tooltip, type } = paramDTO

      if (!isPrimitive(type)) {
        throw Error('Type of input does not match to primitive types')
      }

      return {
        ...paramDTO,
        tooltip: tooltip ? { content: tooltip } : undefined,
        type,
      }
    },
  )

const paramsFromDTO = (
  paramsDTO: DomainPrebidModuleParamsDTO,
  paramsSchema: DomainPrebidModuleSchemaParameter[],
): DomainPrebidModuleParameters =>
  Object.fromEntries(
    paramsSchema.map(
      (
        schema: DomainPrebidModuleSchemaParameter,
      ): [keyof DomainPrebidModuleParameters, DomainPrebidModuleParameter] => {
        const { name, type } = schema

        return [
          name,
          {
            ...schema,
            value: parsePrimitiveValue(paramsDTO[name], type),
          },
        ]
      },
    ),
  )

export const sortToDTO = (
  sort: SortInput<DomainPrebidModuleSortBy>,
): SortInput<DomainPrebidModuleSortByDTO> => {
  const { by } = sort

  if (by === 'domain.name') {
    return { ...sort, by: 'domainName' }
  } else {
    return { ...sort, by }
  }
}
